import { z } from "zod";
import { EVENT_TYPE, OUTCOME } from "../../../constants/eventLog.js";
import { entityId } from "../../common.js";
import { eventLogBaseSchema } from "../base.js";
/**
 * Booking events: what changed on a booking, and the reminders sent for it.
 *
 * One event per kind of change, so a single edit that moves the schedule and swaps the
 * manager writes two rows sharing a `correlationId`. The values come from Sequelize's dirty
 * tracking in the `Booking` afterUpdate hook, so they describe what the row actually did
 * rather than what a request asked for.
 *
 * Every schema here is `.strict()` for the same reason as billing: an audit row cannot be
 * corrected after the fact, so a typo'd key has to fail at the call site.
 */
/**
 * The booking columns this log tracks, old values in `preChange` and new in `postChange`.
 *
 * Sparse on both sides, unlike the charge schemas in `../base.js`: only the fields that drove
 * the event are present, because `changed()` reports exactly those and padding the rest back
 * in would assert that unchanged columns were part of the change.
 *
 * Overrides the base `preChange`/`postChange`, whose shapes describe charge movements and have
 * nothing to say about a booking's own columns.
 */
export const bookingChangeSchema = z
    .object({
    managerId: entityId.nullable().optional(),
    anyManagerAtLocation: z.boolean().nullable().optional(),
    status: z.string().nullable().optional(),
    customStatusId: entityId.nullable().optional(),
    date: z.string().nullable().optional(),
    startHours: z.number().nullable().optional(),
    startMinutes: z.number().nullable().optional(),
    endHours: z.number().nullable().optional(),
    endMinutes: z.number().nullable().optional(),
    serviceId: entityId.nullable().optional(),
    locationId: entityId.nullable().optional(),
})
    .strict();
/**
 * A related record named as it stood when the event was written.
 *
 * Denormalised for the same reason as `actorLabel`: the row may be renamed or deleted later,
 * and the log still has to say which service or location this was at the time. `null` when the
 * field was cleared.
 */
const namedRecordSchema = z
    .object({ id: entityId, name: z.string().nullable() })
    .strict()
    .nullable();
const managerRecordSchema = z
    .object({
    id: entityId,
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
})
    .strict()
    .nullable();
/** Events whose `preChange`/`postChange` already say everything; nothing to denormalise. */
const noMetadataSchema = z.object({}).strict();
const bookingEventBaseSchema = eventLogBaseSchema.extend({
    outcome: z.literal(OUTCOME.APPLIED),
    reasonCode: z.null().optional(),
    preChange: bookingChangeSchema.nullable().optional(),
    postChange: bookingChangeSchema.nullable().optional(),
});
/**
 * Every booking event, in the form the top-level union expects.
 *
 * Exported as an array so `../index.ts` can concatenate it with other features' arrays
 * without naming each member.
 */
export const bookingEventSchemas = [
    bookingEventBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BOOKING_MANAGER_CHANGED),
        metadata: z.object({ manager: managerRecordSchema }).strict(),
    })
        .strict(),
    bookingEventBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BOOKING_STATUS_CHANGED),
        /**
         * Required, and real columns rather than metadata: "how did this booking get to its
         * current status" is the question these rows are read for, and both ends have to be
         * filterable for it to be answerable without scanning JSON.
         */
        fromStatus: z.string().max(50).nullable(),
        toStatus: z.string().max(50).nullable(),
        metadata: noMetadataSchema,
    })
        .strict(),
    bookingEventBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BOOKING_CUSTOM_STATUS_CHANGED),
        metadata: z.object({ customStatus: namedRecordSchema }).strict(),
    })
        .strict(),
    bookingEventBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BOOKING_RESCHEDULED),
        metadata: noMetadataSchema,
    })
        .strict(),
    bookingEventBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BOOKING_SERVICE_CHANGED),
        metadata: z.object({ service: namedRecordSchema }).strict(),
    })
        .strict(),
    bookingEventBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BOOKING_LOCATION_CHANGED),
        metadata: z.object({ location: namedRecordSchema }).strict(),
    })
        .strict(),
    bookingEventBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BOOKING_ANY_MANAGER_AT_LOCATION_CHANGED),
        metadata: noMetadataSchema,
    })
        .strict(),
    bookingEventBaseSchema
        .extend({
        eventType: z.literal(EVENT_TYPE.BOOKING_REMINDER_SENT),
        /** Nothing on the booking changed, so there is no before or after to record. */
        metadata: noMetadataSchema,
    })
        .strict(),
];
//# sourceMappingURL=booking.js.map